import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LabBarChart from './graphs/bar';
import { BaSeOptionPicker, BaSePopupButton } from '@base/react';
import { INDICATORS_MAPPING, FLUXGRAPH_FILTER_OPTIONS } from '../../../mocks/consts';
import LabLineChart from './graphs/line';
import ApiLab from '../../../services/lab';
import SideModal from '../../modals/sidemodal';
import EditLabPlanning from './edit';
import {eventTag, formatCurrency} from '../../../services/utils';
import CreatePlanningBanner from './createPlanning';
import './index.scss';

const apiLab = ApiLab();

const LabResults = ({
                      renderHeader,
                      selectedIndicators,
                      initialLabData,
                      showInformationBox,
                      eventLabGA
                    }) => {
  const navigate = useNavigate();

  const [fluxgraphMonthAmount, setFluxgraphMonthAmount] = useState(12);
  const [labModalVisible, setLabModalVisible] = useState(false);
  const [planToEditData, setPlanToEditData] = useState(null);
  const [clickedMonth, setClickedMonth] = useState(null);
  const [clickedPlanningProfit, setClickedPlanningProfit] = useState(0);
  const [selectedPlanningId, setSelectedPlanningId] = useState(null);
  const [updatedLabData, setUpdatedLabData] = useState(initialLabData);
  const [updatedPlannings, setUpdatedPlannings] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleInteraction = (planningId, month, labId) => {
    setSelectedPlanningId(planningId);

    apiLab.getPlanDataForLab(planningId, labId)
      .then((res) => {
        const rawData = res.data.data;
        const formattedData = {
          ...rawData,
          cif: formatCurrency(rawData.cif),
          cmp: formatCurrency(rawData.cmp),
          cms: formatCurrency(rawData.cms),
          rtp: formatCurrency(rawData.rtp),
          rts: formatCurrency(rawData.rts),
          cgp: formatCurrency(rawData.cgp),
          iif: formatCurrency(rawData.iif),
          gde: rawData.gde?.map(item => ({
            ...item,
            value: formatCurrency(item.value),
          })) || [{ order: 0, days: "", value: 0 }],
        };
        setClickedPlanningProfit(res.data.lucro);
        setPlanToEditData(formattedData);
        setClickedMonth(month);
        setLabModalVisible(true);
      })
      .catch((err) => {
        console.log(err);
      })
  };

  const handleUpdateSuccess = (updatedData) => {
    if (updatedData.data) {
      const formattedData = {
        ...updatedData.data,
        cif: formatCurrency(updatedData.data.cif),
        cmp: formatCurrency(updatedData.data.cmp),
        cms: formatCurrency(updatedData.data.cms),
        rtp: formatCurrency(updatedData.data.rtp),
        rts: formatCurrency(updatedData.data.rts),
        cgp: formatCurrency(updatedData.data.cgp),
        iif: formatCurrency(updatedData.data.iif),
        gde: updatedData.data.gde?.map(item => ({
          ...item,
          value: formatCurrency(item.value),
        })) || [{ order: 0, days: "", value: 0 }],
      };
      setPlanToEditData(formattedData);
    }

    if (updatedData.graphics) {
      setUpdatedLabData(prevLabData => ({
        ...prevLabData,
        graphics: updatedData.graphics,
      }));
    }

    if (updatedData.movements) {
      setUpdatedPlannings(updatedData.movements);
    }

    setLabModalVisible(false);
  };

  const handleCreateNewPlanning = (selectedPlanningIds) => {
    apiLab.createNewPlanningFromLab(updatedLabData.lab_id, { plan_ids: selectedPlanningIds })
      .then(() => {
        eventTag(eventLabGA?.name, eventLabGA?.category, "submit", "Criando novo planejamento")
        navigate(`/inicio`);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const onboardingPlanningList = useMemo(() => [
    { plan_id: 1, nome: "Planejamento A" },
    { plan_id: 2, nome: "Planejamento B" },
    { plan_id: 3, nome: "Planejamento C" },
  ], []);

  return (
    <div className="lab-results-container">
      {renderHeader()}
      {(updatedPlannings.length > 0 || showInformationBox) &&
        <CreatePlanningBanner
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          planningList={showInformationBox ? onboardingPlanningList : updatedPlannings}
          handleCreatePlanning={handleCreateNewPlanning}
          showInformationBox={showInformationBox}
          eventLabGA={eventLabGA}
        />
      }
      <div className='lab-results-content'>
        {selectedIndicators?.map((indicatorId) => {
          const indicator = INDICATORS_MAPPING.find(ind => ind.identifier === indicatorId);
          const indicatorData = updatedLabData.graphics?.[indicatorId];
          const graphics = indicatorData || [];
          const labId = updatedLabData.lab_id;

          if (indicatorId !== 'fluxgraph') {
            return (
              <div className='indicator-bar-container' key={indicatorId}>
                <div className='indicator-bar-container-header'>
                  <h3>{indicator?.title}</h3>
                  {indicator?.tooltip && (
                    <BaSePopupButton
                      type='tooltip'
                      direction='bottom'
                      message={<span dangerouslySetInnerHTML={{ __html: indicator?.tooltip }} />}
                      alertSize={315}
                    />
                  )}
                </div>
                <p>{indicator?.subtitle}</p>
                <LabBarChart
                  graphics={graphics}
                  type={indicator?.type || 'currency'}
                  indicatorKey={indicator.identifier}
                  onBarClick={(planningId, month) => {
                    handleInteraction(planningId, month, labId);
                  }}
                />
              </div>
            );
          }

          return (
            <div className='indicator-line-container' key={indicatorId}>
              <div className='indicator-line-container-header line'>
                <div className='indicator-line-container-header-content'>
                  <div>
                    <h3>{indicator?.title}</h3>
                    {indicator?.tooltip && (
                      <BaSePopupButton
                        type='tooltip'
                        direction='bottom'
                        message={<span dangerouslySetInnerHTML={{ __html: indicator?.tooltip }} />}
                        alertSize={315}
                      />
                    )}
                  </div>
                  <p>{indicator?.subtitle}</p>
                </div>
                {graphics.some(item => item.fluxgraph?.length > 0) &&
                  <BaSeOptionPicker
                    label="Período de projeção"
                    values={FLUXGRAPH_FILTER_OPTIONS}
                    enableSelectAll={false}
                    maxOptionsSelected={1}
                    withFilterText={false}
                    initialSelectedValuesId={FLUXGRAPH_FILTER_OPTIONS[3].id.toString()}
                    onChange={(e) => setFluxgraphMonthAmount(e[0].monthAmt)}
                    width={290}
                  />
                }
              </div>
              <LabLineChart
                graphics={graphics}
                onPointClick={(planningId, month) => {
                  handleInteraction(planningId, month, labId);
                }}
                fluxgraphMonthAmount={fluxgraphMonthAmount}
              />
            </div>
          )
        })}
      </div>

      {labModalVisible && (
        <SideModal
          show={labModalVisible}
          callBackClose={() => {
            setLabModalVisible(false)
            setPlanToEditData(null)
            eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Fechando modal de comparação`)
          }}
          title={planToEditData?.title}
          width={720}
        >
          <EditLabPlanning
            planToEditData={planToEditData}
            labId={updatedLabData.lab_id}
            planningId={selectedPlanningId}
            onUpdateSuccess={handleUpdateSuccess}
            clickedMonth={clickedMonth}
            planningProfit={formatCurrency(clickedPlanningProfit)}
            eventLabGA={eventLabGA}
          />
        </SideModal>
      )}
    </div>
  )
}

export default LabResults;
